export interface BudgetPreviewItem {
    description: string;
    code: string;
    quantity: number | string;
    quantityPresentation: number | string;
    totalQuantity: number | string;
    price: string;
    subtotal: string;
    discount?: number;
}

export interface BudgetPreviewSection {
    title: string;
    items: BudgetPreviewItem[];
    totalFormatted: string;
    isBonification?: boolean;
}

export interface BudgetPreviewData {
    date: string;
    orderCode: string;
    sections: BudgetPreviewSection[];
    logoUrl: string;
    customer: {
        name: string;
        cnpj: string;
        typeLabel: string;
        branch: string;
        address: string;
        paymentCondition: string;
    };
    labels: {
        documentTitle: string;
        date: string;
        orderCode: string;
        client: string;
        cnpj: string;
        clientType: string;
        branch: string;
        address: string;
        paymentCondition: string;
        items: string;
        code: string;
        qtd: string;
        presentationQtd: string;
        amountQtd: string;
        price: string;
        subTotal: string;
        orderAmount: string;
        desc: string;
    };
}

const CONTAINER_ID = 'budget-print-container';
const STYLE_ID = 'budget-print-style';

const escapeHtml = (value: string | number): string => {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const buildSectionHtml = (
    section: BudgetPreviewSection,
    labels: BudgetPreviewData['labels'],
): string => {
    const colCount = 7;

    const itemsRows = section.items
        .map(
            (it) => `
            <tr>
                <td class="col-desc">${escapeHtml(it.description)}</td>
                <td class="col-code">${escapeHtml(it.code)}</td>
                <td class="num">${escapeHtml(it.quantity)}</td>
                <td class="num">${escapeHtml(it.quantityPresentation)}</td>
                <td class="num">${escapeHtml(it.totalQuantity)}</td>
                <td class="num">${escapeHtml(it.price)}</td>
                <td class="num">${escapeHtml(it.subtotal)}</td>
            </tr>`,
        )
        .join('');

    const emptyRow = `
        <tr>
            <td colspan="${colCount}" class="empty-row">—</td>
        </tr>`;

    return `
        <div class="order-section">
            <div class="section-title">${escapeHtml(section.title)}</div>
            <table class="items">
                <thead>
                    <tr>
                        <th class="col-desc">${escapeHtml(labels.items)}</th>
                        <th class="col-code">${escapeHtml(labels.code)}</th>
                        <th class="num">${escapeHtml(labels.qtd)}</th>
                        <th class="num">${escapeHtml(labels.presentationQtd)}</th>
                        <th class="num">${escapeHtml(labels.amountQtd)}</th>
                        <th class="num">${escapeHtml(labels.price)}</th>
                        <th class="num">${escapeHtml(labels.subTotal)}</th>
                    </tr>
                </thead>
                <tbody>
                    ${section.items.length > 0 ? itemsRows : emptyRow}
                </tbody>
            </table>

            <div class="total-row">
                <div class="total-box">
                    <span class="total-label">${escapeHtml(labels.orderAmount)}</span>
                    <span class="total-value">${escapeHtml(section.totalFormatted)}</span>
                </div>
            </div>
        </div>`;
};

const buildBodyContent = (data: BudgetPreviewData): string => {
    const {
        date,
        orderCode,
        logoUrl,
        customer,
        sections,
        labels,
    } = data;

    const sectionsHtml = sections.map((s) => buildSectionHtml(s, labels)).join('');

    return `
    <header class="page-header">
        <div class="brand">
            <img src="${escapeHtml(logoUrl)}" alt="CEVA" />
        </div>
        <div class="title">${escapeHtml(labels.documentTitle)}</div>
        <div class="meta">
            <div>
                <span class="label">${escapeHtml(labels.date)}</span>
                <div class="value">${escapeHtml(date)}</div>
            </div>
            ${
                orderCode
                    ? `<div style="margin-top:4px;">
                            <span class="label">${escapeHtml(labels.orderCode)}</span>
                            <div class="value">${escapeHtml(orderCode)}</div>
                       </div>`
                    : ''
            }
        </div>
    </header>

    <main>
        <section class="customer-card">
            <h2>${escapeHtml(labels.client)}</h2>
            <div class="customer-grid">
                <div class="customer-field full">
                    <span class="label">${escapeHtml(labels.client)}</span>
                    <span class="value">${escapeHtml(customer.name)}</span>
                </div>
                <div class="customer-field">
                    <span class="label">${escapeHtml(labels.cnpj)}</span>
                    <span class="value">${escapeHtml(customer.cnpj || '—')}</span>
                </div>
                <div class="customer-field">
                    <span class="label">${escapeHtml(labels.clientType)}</span>
                    <span class="value">${escapeHtml(customer.typeLabel || '—')}</span>
                </div>
                <div class="customer-field">
                    <span class="label">${escapeHtml(labels.branch)}</span>
                    <span class="value">${escapeHtml(customer.branch || '—')}</span>
                </div>
                <div class="customer-field">
                    <span class="label">${escapeHtml(labels.address)}</span>
                    <span class="value">${escapeHtml(customer.address || '—')}</span>
                </div>
                ${customer.paymentCondition ? `<div class="customer-field full">
                    <span class="label">${escapeHtml(labels.paymentCondition)}</span>
                    <span class="value">${escapeHtml(customer.paymentCondition)}</span>
                </div>` : ''}
            </div>
        </section>

        ${sectionsHtml}
    </main>`;
};

// Todos os seletores são prefixados com #budget-print-container para
// não vazar para fora do orçamento. As regras vivem em @media print
// para só fazerem efeito durante a impressão; no monitor o container
// fica display:none e o app continua intacto.
const buildStyles = (): string => `
    /* Esconde o container no monitor — só aparece no print */
    #${CONTAINER_ID} {
        display: none;
    }

    @media print {
        @page {
            size: A4 portrait;
            margin: 1.5cm;
        }

        /* Esconde TODO o resto do app durante a impressão */
        body > *:not(#${CONTAINER_ID}) {
            display: none !important;
        }

        html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
        }

        #${CONTAINER_ID} {
            display: block !important;
            font-family: 'Helvetica Neue', Arial, sans-serif;
            color: #1f2937;
            font-size: 13px;
            line-height: 1.45;
            background: #ffffff;
        }

        #${CONTAINER_ID} *,
        #${CONTAINER_ID} *::before,
        #${CONTAINER_ID} *::after {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }

        #${CONTAINER_ID} .page-header {
            background: #ffffff;
            border-bottom: 2px solid #013a93;
            padding: 0.05cm 0;
            margin-bottom: 12px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            page-break-inside: avoid;
            break-inside: avoid;
        }

        #${CONTAINER_ID} .page-header .brand {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        #${CONTAINER_ID} .page-header .brand img {
            height: 1.35cm;
            width: auto;
            display: block;
        }

        #${CONTAINER_ID} .page-header .title {
            font-size: 19px;
            font-weight: 700;
            color: #013a93;
            letter-spacing: 2px;
            text-transform: uppercase;
        }

        #${CONTAINER_ID} .page-header .meta {
            text-align: right;
            font-size: 11px;
            color: #4b5563;
        }

        #${CONTAINER_ID} .page-header .meta .label {
            color: #9ca3af;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-size: 10px;
        }

        #${CONTAINER_ID} .page-header .meta .value {
            color: #111827;
            font-weight: 600;
            font-size: 12px;
        }

        #${CONTAINER_ID} main {
            padding-top: 0;
        }

        #${CONTAINER_ID} .customer-card {
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            padding: 12px 16px;
            margin-bottom: 18px;
            margin-top: 16px;
            background: #f9fafb;
            page-break-inside: avoid;
            break-inside: avoid;
        }

        #${CONTAINER_ID} .customer-card h2 {
            margin: 0 0 10px 0;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #013a93;
            font-weight: 700;
        }

        #${CONTAINER_ID} .customer-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 6px 24px;
        }

        #${CONTAINER_ID} .customer-field .label {
            display: block;
            color: #6b7280;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 2px;
        }

        #${CONTAINER_ID} .customer-field .value {
            color: #111827;
            font-weight: 600;
            font-size: 13px;
            word-break: break-word;
        }

        #${CONTAINER_ID} .customer-field.full {
            grid-column: span 2;
        }

        #${CONTAINER_ID} .order-section + .order-section {
            margin-top: 18px;
        }

        #${CONTAINER_ID} .section-title {
            background: #013a93;
            color: #ffffff;
            padding: 7px 14px;
            font-size: 14px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            border-radius: 4px 4px 0 0;
            margin-top: 4px;
            page-break-after: avoid;
            break-after: avoid;
        }

        #${CONTAINER_ID} table.items {
            width: 100%;
            border-collapse: collapse;
            border: 1px solid #e5e7eb;
            border-top: none;
            margin: 0 0 12px 0;
        }

        #${CONTAINER_ID} table.items thead {
            display: table-header-group;
        }

        #${CONTAINER_ID} table.items tbody tr {
            page-break-inside: avoid;
            break-inside: avoid;
        }

        #${CONTAINER_ID} table.items thead th {
            background: #eef2ff;
            color: #1f2937;
            font-weight: 700;
            text-align: left;
            padding: 9px 7px;
            border-bottom: 1px solid #d1d5db;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.4px;
        }

        #${CONTAINER_ID} table.items tbody td {
            padding: 7px;
            border-bottom: 1px solid #f1f5f9;
            font-size: 12.5px;
            vertical-align: top;
        }

        #${CONTAINER_ID} table.items tbody tr:nth-child(even) td {
            background: #fafbff;
        }

        #${CONTAINER_ID} table.items td.num,
        #${CONTAINER_ID} table.items th.num {
            text-align: right;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
        }

        #${CONTAINER_ID} table.items .col-desc {
            width: 22%;
        }

        #${CONTAINER_ID} table.items .col-code {
            width: 11%;
        }

        #${CONTAINER_ID} table.items .empty-row {
            text-align: center;
            color: #9ca3af;
            font-style: italic;
            padding: 14px 6px;
        }

        #${CONTAINER_ID} .total-row {
            display: flex;
            justify-content: flex-end;
            margin-top: 6px;
            page-break-inside: avoid;
            break-inside: avoid;
        }

        #${CONTAINER_ID} .total-row .total-box {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 12px 18px;
            background: #013a93;
            color: #ffffff;
            border-radius: 4px;
            font-size: 15px;
            font-weight: 700;
        }

        #${CONTAINER_ID} .total-row .total-box .total-label {
            text-transform: uppercase;
            font-size: 12px;
            letter-spacing: 1px;
            opacity: 0.85;
        }

        #${CONTAINER_ID} .total-row .total-box .total-value {
            font-size: 18px;
            font-variant-numeric: tabular-nums;
        }
    }
`;

/**
 * Injeta o orçamento no body atual, aciona o diálogo de impressão e
 * remove tudo ao final. Sem popup nem nova janela.
 *
 * Durante a impressão, todo o resto do app é escondido via @media print
 * (`body > *:not(#budget-print-container) { display: none }`). No monitor
 * o container fica display:none e nada do app é afetado.
 */
export const openBudgetPreview = (data: BudgetPreviewData): void => {
    // Remove resíduos de um possível disparo anterior (Ctrl+P abortado, etc.)
    document.getElementById(CONTAINER_ID)?.remove();
    document.getElementById(STYLE_ID)?.remove();

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = buildStyles();
    document.head.appendChild(style);

    const container = document.createElement('div');
    container.id = CONTAINER_ID;
    container.innerHTML = buildBodyContent(data);
    document.body.appendChild(container);

    let cleanedUp = false;
    let printed = false;
    const pendingTimers: ReturnType<typeof setTimeout>[] = [];

    const cleanup = (): void => {
        if (cleanedUp) return;
        cleanedUp = true;
        // Cancela qualquer print agendado ainda não disparado (fallback de
        // 3s ou delay pós-load das imagens), senão o navegador reabre o
        // diálogo depois que o usuário já cancelou.
        pendingTimers.forEach(clearTimeout);
        pendingTimers.length = 0;
        container.remove();
        style.remove();
        window.removeEventListener('afterprint', cleanup);
    };

    // afterprint cobre cancelamento e impressão.
    window.addEventListener('afterprint', cleanup);

    const triggerPrint = (): void => {
        if (printed || cleanedUp) return;
        printed = true;
        // Cancela timers irmãos para que o fallback não tente disparar
        // de novo enquanto/depois do print atual.
        pendingTimers.forEach(clearTimeout);
        pendingTimers.length = 0;
        try {
            // print() bloqueia em Chrome/Edge/Firefox até o usuário sair do
            // diálogo. Em Safari o afterprint cobre o caso assíncrono.
            window.print();
        } catch (e) {
            console.error('[budget-pdf] erro ao imprimir', e);
        } finally {
            cleanup();
        }
    };

    const schedulePrint = (delayMs: number): void => {
        pendingTimers.push(setTimeout(triggerPrint, delayMs));
    };

    const images = Array.from(container.querySelectorAll('img'));

    if (images.length === 0) {
        schedulePrint(100);
        return;
    }

    let remaining = images.length;
    const onDone = (): void => {
        remaining -= 1;
        if (remaining <= 0) {
            schedulePrint(100);
        }
    };

    images.forEach((img) => {
        if (img.complete) {
            onDone();
        } else {
            img.addEventListener('load', onDone);
            img.addEventListener('error', onDone);
        }
    });

    // Fallback: dispara o print mesmo se uma imagem travar.
    schedulePrint(3000);
};
